const VERSION = 1;
const WORD_FIELDS = ['incorrectWords', 'game3Words', 'game4Words', 'game4IncorrectWords'];

export const getVocabularyProgressKey = (grade, topic) =>
  `vocabulary-progress:${VERSION}:${encodeURIComponent(grade)}:${encodeURIComponent(topic)}`;

const wordKey = (word) => JSON.stringify([word.arabic, word.hebrew]);
const wordListSignature = (words) => JSON.stringify(
  words.map(word => [word.arabic, word.hebrew, word.alts || []])
);

const sanitizeProgress = (record, words) => {
  if (!record || record.version !== VERSION || record.wordListSignature !== wordListSignature(words)) return null;
  if (!Number.isInteger(record.completedThrough) || record.completedThrough < 1 || record.completedThrough > 4) return null;
  if (WORD_FIELDS.some(field => !Array.isArray(record[field]))) return null;

  const available = new Map(words.map(word => [wordKey(word), word]));
  const clean = { version: VERSION, wordListSignature: record.wordListSignature, completedThrough: record.completedThrough };
  const cleanWords = (list) => {
    const seen = new Set();
    return list.flatMap(word => {
      if (!word || typeof word.arabic !== 'string' || typeof word.hebrew !== 'string') return [];
      const key = wordKey(word);
      if (!available.has(key) || seen.has(key)) return [];
      seen.add(key);
      return [available.get(key)];
    });
  };
  for (const field of WORD_FIELDS) {
    clean[field] = cleanWords(record[field]);
  }

  if (record.incorrectWordsByStage) {
    if ([1, 2, 3, 4].some(stage => !Array.isArray(record.incorrectWordsByStage[stage]))) return null;
    clean.incorrectWordsByStage = Object.fromEntries(
      [1, 2, 3, 4].map(stage => [stage, cleanWords(record.incorrectWordsByStage[stage])])
    );
  } else {
    // Legacy saves have only a combined list. Retain possible stage-three overlaps until replayed.
    const size = words.length === 18 ? 9 : 10;
    const stageWords = {
      1: words.slice(0, size),
      2: words.slice(size, size * 2),
      3: [...clean.game3Words, ...words.slice(size * 2)]
    };
    clean.incorrectWordsByStage = Object.fromEntries([1, 2, 3].map(stage => {
      const keys = new Set(stageWords[stage].map(wordKey));
      return [stage, clean.incorrectWords.filter(word => keys.has(wordKey(word)))];
    }));
    clean.incorrectWordsByStage[4] = clean.game4IncorrectWords;
  }
  clean.incorrectWords = cleanWords([1, 2, 3].flatMap(stage => clean.incorrectWordsByStage[stage]));
  clean.game4IncorrectWords = clean.incorrectWordsByStage[4];
  if (record.pendingReviewStages !== undefined && (!Array.isArray(record.pendingReviewStages) ||
    record.pendingReviewStages.some(stage => !Number.isInteger(stage) || stage < 1 || stage > 3))) return null;
  // Older saves do not record which stages were replayed after stage four.
  clean.pendingReviewStages = [...new Set(record.pendingReviewStages || [])];
  return clean;
};

export const getUnlockedVocabularyStage = (progress) => Math.min((progress?.completedThrough || 0) + 1, 4);

export function getVocabularyReviewMistakes(progress) {
  if (!progress) return [];
  const candidates = progress.completedThrough < 4
    ? progress.incorrectWords
    : [
      ...progress.game4IncorrectWords,
      ...(progress.pendingReviewStages || []).flatMap(stage => progress.incorrectWordsByStage[stage])
    ];
  return [...new Map(candidates.map(word => [wordKey(word), word])).values()];
}

export function loadVocabularyProgress(grade, topic, words) {
  if (!grade || !topic || !words.length) return null;
  try {
    return sanitizeProgress(JSON.parse(window.localStorage.getItem(getVocabularyProgressKey(grade, topic))), words);
  } catch {
    return null;
  }
}

export function completeVocabularyStage(previous, stage, words, context) {
  if (!Number.isInteger(stage) || stage < 1 || stage > getUnlockedVocabularyStage(previous)) return previous;
  const saved = previous ? sanitizeProgress(previous, words) : null;
  const incorrectWordsByStage = {
    ...(saved?.incorrectWordsByStage || { 1: [], 2: [], 3: [], 4: [] }),
    [stage]: (stage === 4 ? context.game4IncorrectWords : context.incorrectWords) || []
  };
  const record = {
    version: VERSION,
    wordListSignature: wordListSignature(words),
    completedThrough: Math.max(previous?.completedThrough || 0, stage),
    ...Object.fromEntries(WORD_FIELDS.map(field => [field, context[field] || []])),
    incorrectWordsByStage,
    pendingReviewStages: stage === 4 ? [] : [...new Set([...(saved?.pendingReviewStages || []), stage])]
  };
  return sanitizeProgress(record, words);
}

export function saveVocabularyProgress(grade, topic, words, progress) {
  const clean = sanitizeProgress(progress, words);
  if (!grade || !topic || !clean) return false;
  try {
    window.localStorage.setItem(getVocabularyProgressKey(grade, topic), JSON.stringify(clean));
    return true;
  } catch {
    return false;
  }
}
